import React, { useState, useEffect } from "react";
import axios from "axios";
import { Upload, DefaultHttpStack } from "tus-js-client";
import { useNavigate, useLocation } from "react-router-dom";
import ClipLoader from "react-spinners/ClipLoader";

const EditLessonForm = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const lessonId = searchParams.get("id");

  // Lesson fields
  const [title, setTitle] = useState("");
  const [originalVideoUrl, setOriginalVideoUrl] = useState("");

  // Video upload fields
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [videoUrl, setVideoUrl] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [uploadStatus, setUploadStatus] = useState("idle");
  const [isVideoChanged, setIsVideoChanged] = useState(false);

  // Categories
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingLesson, setLoadingLesson] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      setLoadingCategories(true);
      setLoadingLesson(true);

      try {
        // 1. Fetch full category tree
        const categoriesRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/fetch-categories?taxonomy=video`
        );
        const categoriesData =
          categoriesRes.data.data?.terms || categoriesRes.data;
        setCategories(categoriesData);

        // 2. Fetch lesson data
        const lessonRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/lessons/${lessonId}`
        );
        const lessonData = lessonRes.data;
        setTitle(lessonData.title || "");
        setOriginalVideoUrl(lessonData.video_url || "");
        setVideoUrl(lessonData.video_url || "");

        // 3. Fetch selected categories
        const selectedRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/lessons/${lessonId}/categories`
        );

        // Create selectedCategories object
        const selectedCats = {};
        selectedRes.data.forEach((cat) => {
          selectedCats[cat.id] = true;
        });
        setSelectedCategories(selectedCats);
      } catch (err) {
        setError("Failed to load data");
        console.error(err);
      } finally {
        setLoadingCategories(false);
        setLoadingLesson(false);
      }
    };

    if (lessonId) {
      fetchData();
    }
  }, [lessonId]);

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setFile(e.target.files[0]);
      setIsVideoChanged(true);
      setVideoUrl("");
      setError(null);
    }
  };

  const toggleExpand = (catID) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [catID]: !prev[catID],
    }));
  };

  const toggleCategorySelection = (categoryId) => {
    setSelectedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const renderCategoryTree = (category, level = 0) => {
    const hasChildren = category.children && category.children.length > 0;
    const isExpanded = expandedCategories[category.id];
    const isSelectable = category.taxonomy !== null;

    return (
      <div
        key={`cat-${category.id}`}
        className="mb-2"
        style={{ marginLeft: `${level * 16}px` }}
      >
        <div className="d-flex align-items-center">
          {isSelectable ? (
            <>
              <input
                type="checkbox"
                id={`cat-${category.id}`}
                checked={!!selectedCategories[category.id]}
                onChange={() => toggleCategorySelection(category.id)}
                className="form-check-input me-2"
              />
              <label
                htmlFor={`cat-${category.id}`}
                className="form-check-label"
                style={{ cursor: "pointer" }}
              >
                {category.name}
              </label>
            </>
          ) : (
            <div
              className="d-flex align-items-center"
              style={{ cursor: hasChildren ? "pointer" : "default" }}
              onClick={() => hasChildren && toggleExpand(category.id)}
            >
              {hasChildren && (
                <span className="me-2">{isExpanded ? "−" : "+"}</span>
              )}
              <span>{category.name}</span>
            </div>
          )}
        </div>

        {isExpanded &&
          hasChildren &&
          category.children.map((child) =>
            renderCategoryTree(child, level + 1)
          )}
      </div>
    );
  };

  const handleUpdate = async () => {
    if (!title) {
      setError("Please enter a title.");
      return;
    }

    const selectedCategoryIds = Object.keys(selectedCategories).filter(
      (id) => selectedCategories[id]
    );
    if (selectedCategoryIds.length === 0) {
      setError("Please select at least one category.");
      return;
    }

    try {
      let vimeoId = null;

      // Handle video upload if changed
      if (isVideoChanged && file) {
        setUploadStatus("initiating");
        setError(null);

        // Initiate new video upload
        const initResponse = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/upload/lessons/init`,
          {
            title: file.name,
            file_size: file.size,
          }
        );

        const initData = initResponse.data;
        if (!initResponse.data.success) {
          throw new Error(initData.message || "Failed to initiate upload");
        }

        vimeoId = initData.data.vimeo_id;

        // Upload the new video
        setUploadStatus("uploading");
        setUploading(true);
        setProgress(0);

        await new Promise((resolve, reject) => {
          const upload = new Upload(file, {
            endpoint: initData.data.upload_link,
            uploadUrl: initData.data.upload_link,
            retryDelays: [0, 3000, 5000, 10000],
            chunkSize: 5 * 1024 * 1024, // 5MB chunks
            metadata: {
              filename: btoa(unescape(encodeURIComponent(file.name))),
              filetype: btoa(unescape(encodeURIComponent(file.type))),
            },
            headers: {},
            httpStack: new DefaultHttpStack(),
            onError: (error) => {
              console.error("Upload failed:", error);
              setError(`Upload failed: ${error.message}`);
              setUploadStatus("error");
              setUploading(false);
              reject(error);
            },
            onProgress: (bytesUploaded, bytesTotal) => {
              setProgress((bytesUploaded / bytesTotal) * 100);
            },
            onSuccess: () => {
              resolve();
            },
          });

          upload.options.method = "PATCH";
          upload.start();
        });

        // Delete the old video if it exists
        if (originalVideoUrl) {
          try {
            await axios.delete(
              `${process.env.REACT_APP_API_URL}/api/lessons/${lessonId}/video`,
              {
                data: { video_url: originalVideoUrl },
              }
            );
          } catch (deleteError) {
            console.error("Failed to delete old video:", deleteError);
            // Continue with update even if old video deletion fails
          }
        }
      }

      // Update the lesson
      setUploadStatus("updating");

      const updateData = {
        title: title,
        categories: Object.keys(selectedCategories).filter(
          (id) => selectedCategories[id]
        ),
      };

      // Only include vimeoId if we uploaded a new video
      if (isVideoChanged && file && vimeoId) {
        updateData.vimeoId = vimeoId;
      }

      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/lessons/${lessonId}`,
        updateData
      );

      setUploadStatus("completed");
      navigate("/xl-admin/lessons");
    } catch (err) {
      console.error("Update process error:", err);
      setError(err.response?.data?.message || err.message || "Update failed");
      setUploadStatus("error");
      setUploading(false);
    }
  };

  const getButtonText = () => {
    switch (uploadStatus) {
      case "initiating":
        return "Preparing upload...";
      case "uploading":
        return `Uploading... ${Math.round(progress)}%`;
      case "updating":
        return "Updating lesson...";
      case "completed":
        return "Update Complete";
      case "error":
        return "Try Again";
      default:
        return "Update Lesson";
    }
  };

  if (loadingLesson) {
    return (
      <div className="container mt-4">
        <div className="d-flex justify-content-center">
          <ClipLoader
            color="#5cd400"
            loading={true}
            size={50}
            aria-label="Loading Spinner"
            data-testid="loader"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <h2 className="mb-4">Edit Lesson</h2>

      <div className="row">
        <div className="col-md-8">
          <div className="mb-3">
            <label className="form-label">Title:</label>
            <input
              type="text"
              className="form-control"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Video:</label>
            {originalVideoUrl && !isVideoChanged && (
              <div className="mb-2">
                <div className="alert alert-info">
                  Current video:{" "}
                  <a
                    href={originalVideoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View Video
                  </a>
                </div>
                <button
                  className="btn btn-sm btn-outline-danger mb-2"
                  onClick={() => {
                    setFile(null);
                    setIsVideoChanged(true);
                    setVideoUrl("");
                  }}
                >
                  Change Video
                </button>
              </div>
            )}
            {isVideoChanged && (
              <>
                <input
                  type="file"
                  accept="video/mp4,video/quicktime"
                  className="form-control"
                  onChange={handleFileChange}
                />
                {file && (
                  <small className="text-muted">Selected: {file.name}</small>
                )}
                {uploading && (
                  <div className="progress mt-2">
                    <div
                      className="progress-bar progress-bar-striped progress-bar-animated"
                      role="progressbar"
                      style={{ width: `${progress}%` }}
                      aria-valuenow={progress}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    >
                      {progress}%
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {error && (
            <div className="alert alert-danger mb-3" role="alert">
              {error}
            </div>
          )}

          <button
            className="btn btn-primary"
            onClick={handleUpdate}
            disabled={
              uploading ||
              uploadStatus === "initiating" ||
              uploadStatus === "updating"
            }
          >
            {getButtonText()}
          </button>
        </div>

        <div className="col-md-4">
          <div className="card mb-3">
            <div className="card-header">
              <h5 className="mb-0">Categories</h5>
            </div>
            <div
              className="card-body"
              style={{ maxHeight: "300px", overflowY: "auto" }}
            >
              {loadingCategories ? (
                <p>Loading categories...</p>
              ) : (
                <div className="category-tree">
                  {categories
                    .filter((category) => !category.parent_id) // Show only top-level categories
                    .map((category) => renderCategoryTree(category))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditLessonForm;
